<script setup lang="ts">
import { Sparkles, House, Layers, Moon, ShieldCheck, Sun, User } from 'lucide-vue-next';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { computed } from 'vue';
import LocaleToggle from '@/components/LocaleToggle.vue';
import Wordmark from '@/components/Wordmark.vue';
import { useTagline } from '@/composables/useTagline';
import { useAuthStore } from '@/stores/auth';
import { useReadingStore } from '@/stores/reading';

/**
 * Bottom bar on phones, top bar from `sm` up. Mobile-first per the SPEC, and
 * the bar sits inside the safe area so it clears the iOS home indicator.
 *
 * The language toggle sits outside the destination list rather than as one
 * more tab: it is a preference, not a place, and it keeps its natural width
 * so the destinations still divide the rest of a phone screen evenly.
 *
 * From `sm` up the bar also carries the wordmark and the tagline, which is
 * why the pages no longer print their own masthead: on a desktop the bar is
 * already a strip across the top, and a second identical strip below it was
 * spending a whole band of vertical space to say the same thing twice. The
 * bottom bar on a phone is no place for a masthead, so there the home page
 * keeps its header.
 */
const { t } = useI18n();
const route = useRoute();
const tagline = useTagline();
const auth = useAuthStore();
const reading = useReadingStore();

/**
 * The Readings tab always means "a new reading": tapping it puts away the
 * one on screen, even when it is the page already open.
 */
function onNavigate(to: string): void {
  if (to === '/reading') reading.reset();
}

const items = computed(() => [
  { to: '/', label: t('nav.home'), icon: House },
  { to: '/arcana', label: t('nav.arcana'), icon: Layers },
  { to: '/reading', label: t('nav.readings'), icon: Sparkles },
  { to: '/horoscope', label: t('nav.horoscope'), icon: Sun },
  { to: '/dreams', label: t('nav.dreams'), icon: Moon },
  { to: '/account', label: t('nav.account'), icon: User },
  // Operators only. Showing it is cosmetic; the API enforces the role.
  ...(auth.user?.isAdmin ? [{ to: '/admin', label: t('nav.admin'), icon: ShieldCheck }] : []),
]);

// `/` is a prefix of every route, so it is the one entry that has to match
// exactly or it would light up everywhere.
const isActive = (to: string): boolean =>
  to === '/' ? route.path === '/' : route.path.startsWith(to);
</script>

<template>
  <nav
    class="pad-safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-ink-line bg-ink/95 backdrop-blur sm:static sm:border-b sm:border-t-0 sm:pb-0"
    :aria-label="t('nav.home')"
  >
    <div class="mx-auto flex max-w-page items-center">
      <!--
        Desktop masthead. The tagline waits for `xl`: it runs to about forty
        characters, and below that width it would either crowd the
        destinations or have to be truncated mid-sentence, which reads worse
        than not showing it at all — the home page still prints it on a phone.
      -->
      <!-- Named for the app, not "Home": the Home tab is a few pixels to the
           right and two links reading the same is a worse listing than one
           that says which is the masthead. -->
      <RouterLink
        to="/"
        class="hidden shrink-0 items-baseline gap-3 px-4 sm:flex lg:px-6"
        :aria-label="t('app.name')"
      >
        <Wordmark class="text-2xl" />
        <span class="hidden whitespace-nowrap text-sm text-muted xl:inline">{{ tagline }}</span>
      </RouterLink>

      <ul class="flex flex-1 justify-center sm:justify-end">
        <!-- Pinned at 13px on a phone rather than riding the global scale:
             seven controls share this row, and "Horoscope" is already at the
             width the cell can give it. From `sm` the bar is a top strip with
             room to spare, so there the labels scale with everything else. -->
        <li v-for="item in items" :key="item.to" class="min-w-0 flex-1 sm:flex-none">
          <RouterLink
            :to="item.to"
            class="flex flex-col items-center gap-1 px-1.5 py-2.5 text-center text-[0.8125rem] leading-tight font-display transition-colors sm:flex-row sm:gap-2 sm:px-5 sm:text-sm"
            :class="isActive(item.to) ? 'text-gold' : 'text-muted hover:text-parchment'"
            :aria-current="isActive(item.to) ? 'page' : undefined"
            @click="onNavigate(item.to)"
          >
            <component :is="item.icon" class="size-5" aria-hidden="true" />
            {{ item.label }}
          </RouterLink>
        </li>
      </ul>

      <LocaleToggle />
    </div>
  </nav>
</template>
